import { CaretRightOutlined, LeftOutlined, PauseOutlined, RightOutlined } from '@ant-design/icons';
import useSize from '@react-hook/size';
import { scaleTime } from 'd3-scale';
import { DateTime, Duration } from 'luxon';
import React, { useRef, useState, useMemo, useEffect } from 'react';
import styled from 'styled-components';

import { useSettingsContext } from '@mono/platmap-settings-context';
import { toHash } from '@mono/to-hash';

import { TimeLabel } from 'TimeLabel';
import { useTimeContext } from 'time-context';

import { roundToNearestTime } from '@mono/round-time';

const toMillis = (time: string) => DateTime.fromISO(time).toMillis();

const timelinePadding = 3;
const maximumLabelWidth = 80;

export const Timeline = () => {
    const { settings } = useSettingsContext();

    if (settings.timeControl.type !== 'timespan') {
        console.log('this should never happen, and is only checked for typescript type inference.');
        console.log('if this does happen, a developer needs to investigate Platmap, MapWrap, and Timeline.');
        return <div />;
    }

    const markStepToRoundTo = settings.syncTimelineTo ? Duration.fromObject({ minutes: 5 }) : Duration.fromISO(settings.timeControl.markStep);

    const hourAndMinuteFormat = settings.use12HourTimeFormat ? 'h:mm a' : 'H:mm';
    const hourFormat = settings.use12HourTimeFormat ? 'h a' : 'H';

    const { markStep, timeBetweenMarks, autoplay, showTimelineControls, timespan } = settings.timeControl;
    const { selectedTime, setSelectedTime, timePoints, isLoading, now } = useTimeContext();
    const container = useRef(null);
    const [widthWithPadding] = useSize(container);
    const [isPlaying, setIsPlaying] = useState<boolean>(false);
    const [tick, setTick] = useState('the big bang');
    const [consideringPoint, setConsideringPoint] = useState<string>();
    const timePointLineRef = useRef(null);
    const [skipNextTick, setSkipNextTick] = useState(true);
    const [isScrubbing, setIsScrubbing] = useState(false);
    const timePointLineWidth = widthWithPadding - timelinePadding - timelinePadding;

    // This is for the agents while they configure.
    // When they enable autoplay, it will start to play. It sounds small but has a good UX in the Console.
    useEffect(() => {
        setIsPlaying(autoplay);
    }, [autoplay, showTimelineControls]);

    // Pause when things load.
    const isPlayingValidated = useMemo(() => {
        if (isLoading.wxtiles === true) return false;
        if (isLoading.wxtilesV3 === true) return false;
        if (isLoading.lightning === true) return false;
        if (isScrubbing) return false;

        return isPlaying;
    }, [isPlaying, toHash(isLoading), isScrubbing]);

    // This is the tick that will be used for animation.
    useEffect(() => {
        if (!isPlayingValidated) return;
        const interval = setInterval(() => {
            setTick((prev) => toHash(prev)); // blockchain? lolf
        }, timeBetweenMarks);
        return () => clearInterval(interval);
    }, [isPlayingValidated, timeBetweenMarks]);

    const [earliestTime, lastTime] = useMemo(() => {
        const [startDuration, endDuration] = timespan.split('--').map((time) => Duration.fromISO(time));

        const startTime = roundToNearestTime({
            time: DateTime.fromISO(now).plus(startDuration).toISO(),
            roundTo: { unit: 'minute', value: markStepToRoundTo.as('minutes') },
            operation: 'floor',
            timezone: settings.timezone,
        });
        const endTime = roundToNearestTime({
            time: DateTime.fromISO(now).plus(endDuration).toISO(),
            roundTo: { unit: 'minute', value: markStepToRoundTo.as('minutes') },
            operation: 'ceil',
            timezone: settings.timezone,
        });

        return [startTime, endTime];
    }, [settings.syncTimelineTo, timespan, timePoints?.join(), settings.timezone]);

    // These are the labels that will be displayed on some marks.
    // You would think that they map 1-1 to the marks, but the labels take up much more width, so we need logic to skip some if there are too many.
    const timeLabels = useMemo(() => {
        if (settings.timeControl.type !== 'timespan') return [];
        if (!timePoints) return [];
        if (timePointLineWidth < 0) return [];
        if (earliestTime === '' || lastTime === '') return [];

        const timespanInSeconds = DateTime.fromISO(lastTime).diff(DateTime.fromISO(earliestTime)).as('seconds');

        const firstTimePoint = DateTime.fromISO(earliestTime);

        const daySpanning = firstTimePoint.day !== DateTime.fromISO(lastTime).day;

        const numberOfDays = Math.max(Math.ceil(timespanInSeconds / 86400) - 1, 1);

        const firstChangeOfDay = daySpanning ? firstTimePoint.endOf('day').plus({ milliseconds: 1 }) : firstTimePoint;

        const firstDayInSeconds = firstChangeOfDay.diff(firstTimePoint).as('seconds');
        const numberOfLabelsInFirstDay = Math.floor(timePointLineWidth / (timespanInSeconds / firstDayInSeconds) / maximumLabelWidth);

        const endOfDay = firstChangeOfDay.plus({ days: 1 });
        const endOfTimeSection = endOfDay.toISO() > DateTime.fromISO(lastTime).toISO() ? DateTime.fromISO(lastTime) : endOfDay;

        const timeSectionSpanInSeconds = Math.abs(firstChangeOfDay.diff(endOfTimeSection).as('seconds')) || Math.abs(firstDayInSeconds);

        const maxLabelsPerDay = Math.floor((timePointLineWidth * (timeSectionSpanInSeconds / timespanInSeconds)) / maximumLabelWidth);
        const numberOfMarksInDay =
            maxLabelsPerDay > Math.floor((timePointLineWidth * (timeSectionSpanInSeconds / timespanInSeconds)) / 300)
                ? maxLabelsPerDay
                : Math.floor((timePointLineWidth * (timeSectionSpanInSeconds / timespanInSeconds)) / 300);

        //Round to 5 mins
        const dayMarkStepInSeconds = Math.ceil(timeSectionSpanInSeconds / numberOfMarksInDay / 300) * 300;

        const firstSectionOfTimeArray = Array.from({ length: numberOfLabelsInFirstDay }).map((_, i) => {
            const stepTime = firstChangeOfDay.minus({ seconds: (i + 1) * dayMarkStepInSeconds });
            if (stepTime.minute === 0) {
                return {
                    time: stepTime.toISO(),
                    label: stepTime.setZone(settings.timezone).toFormat(hourFormat).toLowerCase(),
                };
            }
            return {
                time: stepTime.toISO(),
                label: stepTime.setZone(settings.timezone).toFormat(hourAndMinuteFormat).toLowerCase(),
            };
        });

        const restOfTimeArray = Array.from({ length: numberOfDays }).flatMap((_, index) => {
            return Array.from({ length: numberOfMarksInDay }).map((_, i) => {
                const stepTime = firstChangeOfDay
                    .setZone(settings.timezone)
                    .plus({ days: index })
                    .plus({ seconds: i * dayMarkStepInSeconds });
                if (stepTime.toISOTime({ includeOffset: false }) === '00:00:00.000')
                    return {
                        time: stepTime.toISO(),
                        label: stepTime.setZone(settings.timezone).toFormat('EEE dd'),
                    };
                if (i === 0 && daySpanning) {
                    return {
                        time: stepTime.toISO(),
                        label: stepTime.setZone(settings.timezone).toFormat('EEE dd'),
                    };
                }
                if (stepTime.minute === 0) {
                    return {
                        time: stepTime.toISO(),
                        label: stepTime.setZone(settings.timezone).toFormat(hourFormat).toLowerCase(),
                    };
                }
                return {
                    time: stepTime.toISO(),
                    label: stepTime.setZone(settings.timezone).toFormat(hourAndMinuteFormat).toLowerCase(),
                };
            });
        });

        return [...firstSectionOfTimeArray, ...restOfTimeArray].filter((a): a is NonNullable<typeof a> => !!a);
    }, [
        timePoints?.join(),
        timeBetweenMarks,
        timePointLineWidth,
        timespan,
        markStep,
        now,
        earliestTime,
        lastTime,
        settings.timezone,
        settings.use12HourTimeFormat,
    ]);

    const [timeToX, xToTime] = useMemo(() => {
        const timeToXScale = scaleTime()
            .domain([earliestTime, lastTime].map(toMillis))
            .range([timelinePadding * 2, timePointLineWidth - timelinePadding * 2]);
        const timeToX = (time: string) => Math.floor(timeToXScale(toMillis(time)));

        const xToTimeScale = scaleTime()
            .domain([timelinePadding * 2, timePointLineWidth - timelinePadding * 2])
            .range([earliestTime, lastTime].map(toMillis));
        const xToTime = (x: number) => DateTime.fromMillis(xToTimeScale(x)).setZone(settings.timezone).toISO();
        return [timeToX, xToTime];
    }, [earliestTime, lastTime, timePointLineWidth, settings.timezone]);

    // If the timepoints change for any reason (like time passes and our timeline updates), then restart from t0
    useEffect(() => {
        if (!timePoints || timePoints.length === 0) return;
        setSelectedTime(timePoints[0]);
    }, [timePoints?.join()]);

    // This is where the animation happens, ultimately it just sets the selectedTime provided by the timeContext
    useEffect(() => {
        if (skipNextTick) {
            setSkipNextTick(false);
            return;
        }
        if (!isPlayingValidated) return;
        if (!timePoints) return;

        if (!selectedTime) {
            setSelectedTime(timePoints[0]);
            return;
        }

        const currentMarkIndex = timePoints.findIndex((timePoint) => timePoint === selectedTime);
        if (currentMarkIndex === -1) {
            setSelectedTime(timePoints[0]);
            return;
        }

        const nextMark = timePoints[(currentMarkIndex + 1) % timePoints.length];
        setSelectedTime(nextMark);
    }, [tick]);

    // This is where the scrubbing happens, also disables animation
    useEffect(() => {
        if (!isScrubbing) return;
        if (!consideringPoint) return;

        if (showTimelineControls && isPlaying) setIsPlaying(false);
        if (selectedTime === consideringPoint) return;

        setSelectedTime(consideringPoint);
    }, [isScrubbing, consideringPoint]);

    useEffect(() => {
        const stopScrubbing = () => setIsScrubbing(false);
        document.body.addEventListener('mouseup', stopScrubbing);
    }, []);

    return (
        <TimeComponent
            className='Timeline'
            style={{
                width: '100%',
                display: 'flex',
                flexDirection: 'row',
                gap: 8,
                alignItems: 'center',
                padding: 12,
                fontFamily: 'Open Sans, sans-serif',
                position: 'relative',
            }}
            onMouseMove={(e) => {
                if (!timePointLineRef.current) return;
                if (!timePoints) return;

                const timeline = timePointLineRef.current as HTMLElement;
                const timelineLength = timeline.getBoundingClientRect().width;
                const timeDistance = DateTime.fromISO(timePoints[timePoints.length - 1]).toSeconds() - DateTime.fromISO(timePoints[0]).toSeconds();
                const timeStepSeconds = timeDistance / timelineLength;

                const mouseXPosition = e.clientX - timeline.getBoundingClientRect().left;
                if (mouseXPosition <= 0) return;
                const mouseTimePoint = DateTime.fromISO(xToTime(mouseXPosition))
                    .setZone(settings.timezone)
                    .minus({
                        seconds: timeStepSeconds,
                    })
                    .toISO();
                const nearestTimePointIndex = timePoints.findIndex((timePoint) => timePoint >= mouseTimePoint);
                const [pointA, pointB] = [
                    DateTime.fromISO(timePoints[nearestTimePointIndex]).setZone(settings.timezone),
                    DateTime.fromISO(timePoints[nearestTimePointIndex - 1]).setZone(settings.timezone),
                ];

                const nearestTimePoint =
                    DateTime.fromISO(mouseTimePoint).setZone(settings.timezone).diff(pointA).as('seconds') >
                    pointB.diff(DateTime.fromISO(mouseTimePoint).setZone(settings.timezone)).as('seconds')
                        ? pointA.toISO()
                        : pointB.toISO();

                if (!nearestTimePoint) return;

                if (isScrubbing && nearestTimePoint !== selectedTime) {
                    setSelectedTime(nearestTimePoint);
                    setConsideringPoint(undefined);
                    return;
                }
                setConsideringPoint(nearestTimePoint);
            }}
            onMouseDown={(e) => {
                if (!timePointLineRef.current) return;
                const mouseXPosition = e.clientX - (timePointLineRef.current as HTMLElement).getBoundingClientRect().left;
                if (mouseXPosition <= 0) return;

                setIsScrubbing(true);
                e.preventDefault();
            }}
            onMouseOut={() => {
                setConsideringPoint(undefined);
            }}
        >
            {showTimelineControls === false && <TimeLabel />}
            {showTimelineControls && (
                <div
                    style={{
                        display: 'flex',
                        flexDirection: 'row',
                        alignItems: 'center',
                        gap: 8,
                        padding: 4,
                        borderRadius: 37,
                        backgroundColor: 'white',
                        boxShadow: '0px 0px 4px rgba(0, 0, 0, 0.3)',
                    }}
                >
                    <TimeButton
                        onMouseOut={() => {
                            setConsideringPoint(undefined);
                        }}
                        onClick={() => {
                            if (!timePoints) return;
                            const currentTimePointIndex = timePoints.findIndex((timePoint) => timePoint === selectedTime);

                            const previousTimePointIndex = currentTimePointIndex === 0 ? timePoints.length - 1 : currentTimePointIndex - 1;
                            setSelectedTime(timePoints[previousTimePointIndex]);
                            setIsPlaying(false);

                            const nextPreviousTimePoint = previousTimePointIndex === 0 ? timePoints.length - 1 : previousTimePointIndex - 1;

                            setConsideringPoint(timePoints[nextPreviousTimePoint]);
                        }}
                        onMouseOver={() => {
                            if (!timePoints) return;
                            const currentTimePointIndex = timePoints.findIndex((timePoint) => timePoint === selectedTime);
                            const previousTimePoint = currentTimePointIndex === 0 ? timePoints[timePoints.length - 1] : timePoints[currentTimePointIndex - 1];

                            setConsideringPoint(previousTimePoint);
                        }}
                    >
                        <LeftOutlined
                            style={{
                                pointerEvents: 'none',
                            }}
                        />
                    </TimeButton>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 0, width: 66, alignItems: 'center', lineHeight: 1 }}>
                        <div style={{ fontSize: 16 }}>
                            <span style={{ fontWeight: 700 }}>
                                {selectedTime &&
                                    DateTime.fromISO(selectedTime)
                                        .setZone(settings.timezone)
                                        .toFormat(`${settings.use12HourTimeFormat ? 'h' : 'H'}:mm`)}
                            </span>
                            {settings.use12HourTimeFormat && (
                                <span>{selectedTime && DateTime.fromISO(selectedTime).setZone(settings.timezone).toFormat('a').toLowerCase()}</span>
                            )}
                        </div>
                        <div style={{ fontSize: 14 }}>{selectedTime && DateTime.fromISO(selectedTime).setZone(settings.timezone).toFormat('dd MMM')}</div>
                    </div>
                    <TimeButton
                        onMouseOut={() => {
                            setConsideringPoint(undefined);
                        }}
                        onClick={() => {
                            if (!timePoints) return;
                            setSelectedTime(consideringPoint);
                            setIsPlaying(false);

                            if (consideringPoint === timePoints[timePoints.length - 1]) {
                                setConsideringPoint(timePoints[0]);
                                return;
                            }

                            setConsideringPoint(timePoints[timePoints.findIndex((timePoint) => timePoint === consideringPoint) + 1]);
                        }}
                        onMouseEnter={() => {
                            if (!timePoints) return;

                            if (selectedTime === timePoints[timePoints.length - 1]) {
                                setConsideringPoint(timePoints[0]);
                                return;
                            }

                            setConsideringPoint(timePoints[timePoints.findIndex((timePoint) => timePoint === selectedTime) + 1]);
                        }}
                    >
                        <RightOutlined
                            style={{
                                pointerEvents: 'none',
                            }}
                        />
                    </TimeButton>
                    <PlayButton
                        onClick={(e) => {
                            setIsPlaying(!isPlaying);
                        }}
                    >
                        {isPlaying ? <PauseOutlined /> : <CaretRightOutlined />}
                    </PlayButton>
                </div>
            )}
            <TimelineWrapper
                ref={container}
                className='TimelineWrapper'
                style={{
                    // I have no idea why the minWidth is needed, but without it the timeline goes outside the flex boundary.
                    // Feel free to try and remove this and see what happens.
                    flex: 1,
                    minWidth: 0,
                    position: 'relative',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                }}
            >
                <div
                    style={{
                        width: timePointLineWidth,
                        display: 'flex',
                        pointerEvents: 'none',
                        position: 'absolute',
                        top: -20,
                    }}
                >
                    {timeLabels.map(({ time, label }, i) => {
                        if (!time) return;
                        const x = timeToX(time);
                        // This condition checks if the label text is going to bleed off the edge of the timeline.
                        // Is it alright to render if just a lil bit goes off the end? Just the empty space not the words?
                        if (x > timePointLineWidth || x < 0) return null;
                        return (
                            <div
                                key={time}
                                style={{
                                    height: 16,
                                    display: 'flex',
                                    flexDirection: 'row',
                                    gap: 4,
                                    position: 'absolute',
                                    left: x,
                                    alignItems: 'center',
                                }}
                            >
                                <svg width={1} height={15} aria-label='dash' role='img'>
                                    <rect x={0} y={0} width={1} height={15} fill='white' />
                                </svg>
                                {x < timePointLineWidth - 60 && timeLabels.length - 1 && (
                                    <div
                                        style={{
                                            fontSize: 16,
                                            whiteSpace: 'nowrap',
                                            fontWeight: 600,
                                            textShadow: '0px 0px 4px rgba(0, 0, 0, 0.4)',
                                            color: 'white',
                                        }}
                                    >
                                        {label}
                                    </div>
                                )}
                            </div>
                        );
                    })}
                </div>
                <TimePointLine width={widthWithPadding} ref={timePointLineRef}>
                    {selectedTime && (
                        <SelectedTimeSpan
                            shouldAnimate={!isScrubbing}
                            x={timelinePadding}
                            y={3}
                            width={timeToX(selectedTime) + timelinePadding}
                            rx={4}
                            height={8}
                            fill='#7599FF'
                        />
                    )}
                    {timePoints?.map((timePoint) => {
                        const x = timeToX(timePoint) + timelinePadding;
                        const fill = generateFill({ selectedTime, consideringTime: consideringPoint, timePoint });
                        const hovered = consideringPoint === timePoint;
                        const radius = selectedTime === timePoint || hovered ? 5 : 3;

                        return <TimePoints key={timePoint} cx={x} cy={7} r={radius} fill={fill} transitionTime={hovered ? 0.05 : 0.2} />;
                    })}
                </TimePointLine>
            </TimelineWrapper>
        </TimeComponent>
    );
};

interface generateFillProps {
    selectedTime?: string;
    consideringTime?: string;
    timePoint: string;
}
const generateFill = ({ selectedTime, consideringTime, timePoint }: generateFillProps) => {
    if (selectedTime && timePoint <= selectedTime) return '#172344';
    if (consideringTime === timePoint) return '#172344';
    return '#8B8B8B';
};

const TimeComponent = styled.div`
    :hover {
        cursor: pointer;
    }
`;

const SelectedTimeSpan = styled.rect<{ shouldAnimate: boolean }>`
    transition: ${(props) => (props.shouldAnimate ? '0.2s cubic-bezier(1, 0, 0, 1)' : 'none')};
`;
const TimePoints = styled.circle<{ transitionTime: number }>`
    transition: ${(props) => props.transitionTime}s cubic-bezier(1, 0.5, 0, 2);
`;

const TimelineWrapper = styled.div`
    border-radius: 8px;
    background-color: white;
    box-shadow: 0px 0px 4px rgba(0, 0, 0, 0.3);
`;

const TimePointLine = styled.svg<{ width: number }>`
    width: ${(props) => props.width}px;
    height: 14px;
`;

const TimeButton = styled.div`
    border-radius: 50%;
    width: 40px;
    height: 40px;
    display: flex;
    align-items: center;
    justify-content: center;

    :hover {
        background-color: #f0f0f0;
        cursor: pointer;
    }
`;

const PlayButton = styled.div`
    border-radius: 50%;
    width: 40px;
    height: 40px;
    display: flex;
    align-items: center;
    justify-content: center;
    background-color: #7599ff;
    color: white;
    font-size: 18px;

    :hover {
        background-color: #577ce7;
        cursor: pointer;
    }
`;
